import { apiClient } from '../services/api';

/**
 * VAPID base64 key'i Uint8Array'e çevirir (Web Push standardı gerektirir)
 */
export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Kullanıcıyı push notification'a abone eder ve backend'e kaydeder.
 * @param {Object} params
 * @param {string} params.role - 'user' veya 'admin'
 * @param {string} [params.userId] - Opsiyonel kullanıcı ID
 * @param {string} [params.email] - Opsiyonel email
 * @returns {Promise<PushSubscription>} subscription objesi
 */
export const subscribeToPush = async ({ role = 'user', userId = null, email = null } = {}) => {
  // 1. Tarayıcı desteği kontrol
  if (!('Notification' in window)) {
    throw new Error('Bu tarayıcı bildirimleri desteklemiyor.');
  }
  if (!('serviceWorker' in navigator)) {
    throw new Error('Bu tarayıcı Service Worker desteklemiyor.');
  }
  if (!('PushManager' in window)) {
    throw new Error('Bu tarayıcı Push Notifications desteklemiyor.');
  }

  // 2. Service worker kaydı ve ready kontrolü (5 sn zaman aşımı garantili)
  let registration;
  try {
    const swTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Service Worker yanıt vermedi.')), 5000)
    );
    registration = await Promise.race([navigator.serviceWorker.ready, swTimeout]);
  } catch (err) {
    // Eğer ready takıldıysa tekrar register etmeyi dene
    registration = await navigator.serviceWorker.register('/service-worker.js').catch(() => null);
  }

  if (!registration) {
    throw new Error('Service Worker kaydı aktifleşemedi.');
  }

  // 3. Backend'den VAPID public key al
  let vapidPublicKey;
  try {
    const vapidData = await apiClient.get('/push/public-key');
    vapidPublicKey = vapidData?.publicKey;
  } catch (e) {
    // Fallback key
    vapidPublicKey = 'BJvhNzH2qRA-WayQTg1EBcgr_gA6ifwHOOdLa_HzL3Lad8d4l27JvMf4ZQZ8nSGNgAZL-jf0Ooys4-mzVWfQpF4';
  }

  if (!vapidPublicKey) {
    throw new Error('VAPID sunucu anahtarı alınamadı.');
  }

  // 4. Mevcut subscription var mı bak, yoksa yeni oluştur
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  // 5. Backend'e kaydet
  const subJson = subscription.toJSON();
  
  const requestBody = {
    subscription: {
      endpoint: subJson.endpoint,
      keys: {
        p256dh: subJson.keys?.p256dh,
        auth: subJson.keys?.auth,
      },
    },
    role,
    // ✅ FIX: undefined yerine null gönder
    userId: userId || null,
    email: email || null,
  };
  
  await apiClient.post('/push/subscribe', requestBody).catch((err) => {
    throw err;
  });
  
  return subscription;
};

/**
 * Aktif push aboneliğinin endpoint'ini döner (email/userId olmadan misafir hedefleme için).
 * @returns {Promise<string|null>}
 */
export const getActivePushEndpoint = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription?.endpoint || null;
  } catch {
    return null;
  }
};

/**
 * Aktif push aboneliği var mı kontrol eder (tarayıcı izninden bağımsız).
 * @returns {Promise<boolean>}
 */
export const getIsPushSubscribed = async () => {
  const endpoint = await getActivePushEndpoint();
  return !!endpoint;
};

/**
 * Push aboneliğini tarayıcıdan ve backend'den kaldırır.
 * Not: Tarayıcı bildirim izni (Notification.permission) programatik olarak sıfırlanamaz;
 * sadece push subscription kaldırılır — bildirimler cihaz/backend tarafında durur.
 */
export const unsubscribePush = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await apiClient.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
    await subscription.unsubscribe();
  }
};

/**
 * Push notification iznini kontrol eder ve ister.
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';

  try {
    return await Notification.requestPermission();
  } catch (e) {
    // Eski Safari callback uyumluluğu
    return new Promise((resolve) => {
      Notification.requestPermission((permission) => {
        resolve(permission);
      });
    });
  }
};
